import { FC, useEffect, useRef, useState } from "react";
import { HexColorPicker, HexColorInput } from "react-colorful";
import styled from 'styled-components/macro';
import useDropdown from "../../../hooks/useDropdown";

const ColorPickerContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10px;
    background-color: white;
    z-index: 2;
    border-radius: 5px;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
    padding: 10px;
`;

const ColorContainer = styled.div<{ color: string }>`
    width: 100%;
    height: 40px;
    border: 1px solid #DDD;
    background-color: ${props => props.color};
    cursor: pointer;

    @media(hover) {
        &:hover {
            opacity: 0.7;
        }
    }
`;

const HexPickerContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const HexInput = styled(HexColorInput)`
    width: 200px;
    background-color: transparent;
    color: #414042;
    border: 1px #f4f4f4 solid;
    font-family: "Montserrat",sans-serif;
    outline:none;
    resize: none;

  &:hover{
    border: 1px black solid;
  }

  &:focus{
    border: 1px black solid;
    outline:none;
  }
`;

const RgbColorsContainer = styled.div`
    width: 200px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 3px;
`;

const RGBSingleColor = styled.div`
    display: flex;
    flex-flow: row;
`;

const RgbInput = styled.input`
    width: 55px; 
    background-color: transparent;
    color: #414042;
    border: 1px #f4f4f4 solid;
    font-family: "Montserrat",sans-serif;
    outline:none;
    resize: none;

  &:hover{
    border: 1px black solid;
  }

  &:focus{
    border: 1px black solid;
    outline:none;
  }
`;

const RgbLabel = styled.span`
    margin: 0px 2px 3px 0px;
`;


const hexToRgb = (hex: string) => {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
}

const componentToHex = (c: number) => {
    var hex = c.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
}

const rgbToHex = (rgbColor: { r: number, g: number, b: number }) => {
    return "#" + componentToHex(rgbColor.r) + componentToHex(rgbColor.g) + componentToHex(rgbColor.b);
}

interface ColorPickerDropdownProps {
    color: string,
    onChange: (color: string) => void
}

const valueToColor = (value: string) => {
    let color = parseInt(value, 10);

    if (isNaN(color))
        color = 0;

    if (color < 0)
        color = 0;

    if (color > 255)
        color = 255;

    return color;
}

const clampValue = (value: string) => {
    let color = parseInt(value, 10);

    if (!isNaN(color) && color < 0)
        return '0';

    if (!isNaN(color) && color > 255)
        return '255';

    return value;
}

const ColorPickerDropdown: FC<ColorPickerDropdownProps> = ({ color, onChange }) => {

    const [preventChange, setPreventChange] = useState(false);

    const [r, setR] = useState(hexToRgb(color).r.toString());
    const [g, setG] = useState(hexToRgb(color).g.toString());
    const [b, setB] = useState(hexToRgb(color).b.toString());

    useEffect(() => {
        if (preventChange)
            return;

        setPreventChange(true);
        let rbgNewColor = {
            r: valueToColor(r),
            g: valueToColor(g),
            b: valueToColor(b)
        };

        changeColorFromInputs(rbgNewColor);
        setPreventChange(false);
    }, [r, g, b]);

    useEffect(() => {
        if (preventChange)
            return;

        if (color.length === 7) {
            setPreventChange(true);
            setR(hexToRgb(color).r.toString());
            setG(hexToRgb(color).g.toString());
            setB(hexToRgb(color).b.toString());
            setPreventChange(false);
        }
    }, [color]);

    const changeColorFromInputs = (color: string | { r: number, g: number, b: number }) => {
        if (typeof color === 'object')
            onChange(rgbToHex(color));
        else {
            onChange(color);
        }
    }

    return <ColorPickerContainer>
        <HexPickerContainer>
            <HexColorPicker color={color} onChange={changeColorFromInputs} />
            <HexInput color={color} prefixed onChange={changeColorFromInputs} />
            <RgbColorsContainer>
                <RGBSingleColor>
                    <RgbLabel>r</RgbLabel>
                    <RgbInput type={"number"} value={r} min={0} max={255}
                        onChange={(e) => {
                            setR(clampValue(e.currentTarget.value));
                        }} />
                </RGBSingleColor>
                <RGBSingleColor>
                    <RgbLabel>g</RgbLabel>
                    <RgbInput type={"number"} value={g} maxLength={3}
                        onChange={(e) => {
                            setG(clampValue(e.currentTarget.value));
                        }} />
                </RGBSingleColor>
                <RGBSingleColor>
                    <RgbLabel>b</RgbLabel>
                    <RgbInput type={"number"} value={b} maxLength={3}
                        onChange={(e) => {
                            setB(clampValue(e.currentTarget.value));
                        }} />
                </RGBSingleColor>
            </RgbColorsContainer>
        </HexPickerContainer>
    </ColorPickerContainer>;
}

interface ColorPickerProps {
    color: string,
    onChange: (color: string) => void
}

const ColorPicker: FC<ColorPickerProps> = ({ color, onChange }) => {
    const [open, , isOpened, Dropdown] = useDropdown();
    const ref = useRef<HTMLDivElement>(null);

    return <>
        <ColorContainer
            ref={ref}
            color={color}
            onClick={() => open(ref.current!, 'bottom', 'right')}
        />

        {isOpened && <Dropdown>
            <ColorPickerDropdown color={color} onChange={onChange} />
        </Dropdown>}
    </>;
}

export default ColorPicker;